import React, { useRef } from 'react';
import { ListColumnType, ListFilterType } from 'types/ListTypes';
import AbstractList from 'components/lists/AbstractList';
import { useTranslation } from 'react-i18next';
import { useSettingsDivision } from 'components/settings/SettingsDivisionContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { hasAnyAllowedRight } from 'services/permissions/permissionChecks';
import SubcontractorForm from './SubcontractorForm';
import DayPassOrderModal from './DayPassOrderModal';
import { DayPassOverview } from './SubcontractorTypes';

interface SubcontractorListProps {
    overview: DayPassOverview | null;
    refreshOverview: () => void;
}

// The list of subcontractor users below the day pass card
const SubcontractorList: React.FC<SubcontractorListProps> = ({ overview, refreshOverview }) => {
    const { t } = useTranslation();
    const refetchListRef = useRef<() => void>();

    // Filter the list on the division selected in the settings menu
    const { selectedDivision, isEnvironmentSelected } = useSettingsDivision();
    const divisionParams = !isEnvironmentSelected && selectedDivision ? { 'division_scope': selectedDivision.id } : {};

    // Specify the options of this list:
    const query = {
        endpoint: 'get_user_list',
        defaultParams: { 'is_active': true, 'subcontractors_only': true, ...divisionParams },
        object: 'subcontractor'
    }
    const objectName = 'subcontractor.general.object_name'
    const showSearch = false
    const showDeleteOrDeactivate = 'flag_deactivated'
    const isPaginated = false

    // Specify form modal for this list
    const formModal = <SubcontractorForm />;
    const onRowClick = 'editModal'

    // Platform accounts add subcontractors to an environment without a bundle
    const { allowedRights = [] } = useAuthContext();
    const isPlatformAccount = hasAnyAllowedRight(allowedRights, ['no_user', 'staff_user']);

    // Determine whether the environment has no day passes left to add a subcontractor
    const hasNoPasses = !isPlatformAccount && overview !== null && overview.balance <= 0;

    // Specify the columns of this list:
    const columns: ListColumnType[] = [
        { width: '2fr', field: 'full_name', label: 'user.general.full_name_label' },
        { width: '2fr', field: 'company_name', label: 'subcontractor.general.company_name_label' },
        { width: '2fr', field: 'email', label: 'user.general.username_label' },
    ];

    // Specify the filters of this list:
    const filters: ListFilterType[] = [
        {
            id: 'active',
            label: 'user.general.active_users_label',
            params: { 'is_active': true, 'subcontractors_only': true }
        },
        {
            id: 'inactive',
            label: 'user.general.inactive_users_label',
            params: { 'is_active': false, 'subcontractors_only': true }
        }
    ];

    // Show the order modal instead of the form when no day passes are left
    const getDynamicModal = () => {
        if (hasNoPasses) {
            return <DayPassOrderModal bundles={overview?.bundles} onOrderCompleted={refreshOverview} />;
        }
        return <SubcontractorForm />;
    };

    // Set the modal size based on the dynamic modal
    const getDynamicModalSize = (): 'small' | 'small-medium' | 'medium' | 'large' | 'xtra-large' => {
        return hasNoPasses ? 'medium' : 'small';
    };

    // Set the modal title based on the dynamic modal
    const getDynamicModalTitle = () => {
        if (hasNoPasses) {
            return t('subcontractor.day_pass.order_modal_title');
        }
        return t('button.add_object_label', { object_name: t(`${objectName}.singular`) });
    };

    return (
        <AbstractList
            key={!isEnvironmentSelected && selectedDivision ? selectedDivision.id : 'environment'}
            query={query}
            objectName={objectName}
            filters={filters}
            columns={columns}
            showSearch={showSearch}
            showDeleteOrDeactivate={showDeleteOrDeactivate}
            formModal={formModal}
            isPaginated={isPaginated}
            onRowClick={onRowClick}
            getFormModal={getDynamicModal}
            getFormModalTitle={getDynamicModalTitle}
            getModalSize={getDynamicModalSize}
            setRefetchList={(fn) => { refetchListRef.current = fn; }}
        />
    );
};

export default SubcontractorList;
